import React, {useEffect, useMemo, useState} from "react";
import {useNavigate, useSearchParams} from "react-router-dom";
import {faBook, faFileLines} from "@fortawesome/free-solid-svg-icons";
import {useUserPublicCatalog} from "../../store/user";
import {useCart} from "../CardContext";
import LearnerShell from "./LearnerShell";
import ResourceCard from "./ResourceCard";
import Paginator from "./Paginator";
import EmptyState from "./EmptyState";
import Toast from "../Toast";
import {imageUrl, shortDate} from "../../lib/format";
import {materialDetailPath, mapPublicResource} from "../landing/publicMaterialMap";

const LIMIT = 12;

/** Public book/journal listings. Authentication is deferred until checkout. */
export default function PublicBookJournalCatalog({kind}) {
  const isJournal = kind === "journal";
  const type = isJournal ? "journals" : "books";
  const title = isJournal ? "Journals" : "Books";
  const icon = isJournal ? faFileLines : faBook;
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const {addToCart} = useCart();
  const {
    publicResources, publicResourcesLoading, publicResourcesError,
    publicResourcesTotal, publicResourcesPages, fetchPublicResources,
  } = useUserPublicCatalog();
  const [toast, setToast] = useState(null);
  const page = Math.max(1, Number(searchParams.get("page")) || 1);
  const search = searchParams.get("search") || "";
  const sort = searchParams.get("sort") || "newest";
  const [searchInput, setSearchInput] = useState(search);

  useEffect(() => setSearchInput(search), [search]);
  useEffect(() => {
    fetchPublicResources({page, limit: LIMIT, sort, type});
  }, [fetchPublicResources, page, sort, type]);

  const items = useMemo(() => (publicResources || []).map(mapPublicResource), [publicResources]);
  const filteredItems = useMemo(() => {
    const term = search.trim().toLowerCase();
    return term ? items.filter((item) => `${item.title} ${item.subtitle} ${item.fieldOfStudy}`.toLowerCase().includes(term)) : items;
  }, [items, search]);

  const setParam = (key, value) => {
    const next = new URLSearchParams(searchParams);
    if (value) next.set(key, value);
    else next.delete(key);
    if (key !== "page") next.delete("page");
    setSearchParams(next, {replace: true});
  };

  const addItem = (item) => {
    addToCart({
      id: item.id,
      type: isJournal ? "journal" : "book",
      title: item.title,
      instructor: item.author || item.subtitle || "",
      thumbnail: item.image || "/images/course_placeholder.jpg",
      category: title.slice(0, -1),
      price: item.price,
    });
    setToast(`Added “${item.title}” to your cart`);
  };

  const buyNow = (item) => {
    addItem(item);
    const checkoutType = isJournal ? "journal" : "book";
    navigate(`/cart?checkoutId=${encodeURIComponent(item.id)}&checkoutType=${checkoutType}`);
  };

  return (
    <LearnerShell
      title={title}
      courseNavbar
      subtitle={`Browse academic ${title.toLowerCase()} freely. Sign in when you are ready to checkout.`}
      icon={icon}
      actions={
        <>
          <form onSubmit={(event) => { event.preventDefault(); setParam("search", searchInput.trim()); }}>
            <input value={searchInput} onChange={(event) => setSearchInput(event.target.value)} placeholder={`Search ${title.toLowerCase()}`} aria-label={`Search ${title.toLowerCase()}`} className="w-52 rounded-md border border-slate-200 px-3 py-2 text-sm text-slate-800 sm:w-64" />
          </form>
          <select value={sort} onChange={(event) => setParam("sort", event.target.value)} aria-label={`Sort ${title.toLowerCase()}`} className="rounded-md border border-slate-200 px-3 py-2 text-sm text-slate-700">
            <option value="newest">Newest</option>
            <option value="oldest">Oldest</option>
            <option value="price_asc">Price: low to high</option>
            <option value="price_desc">Price: high to low</option>
            <option value="popular">Most popular</option>
          </select>
        </>
      }
    >
      {toast ? <Toast message={toast} onClose={() => setToast(null)} isSuccess /> : null}
      {publicResourcesError ? <p role="alert" className="mb-4 rounded-lg bg-amber-50 px-3 py-2 text-sm text-amber-800">{publicResourcesError}</p> : null}
      <p className="mb-3 text-xs font-medium uppercase tracking-wide text-slate-500">{publicResourcesTotal} {title.toLowerCase()} available</p>
      {publicResourcesLoading && items.length === 0 ? (
        <div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">{Array.from({length: 8}, (_, index) => <div key={index} className="h-80 animate-pulse rounded-xl border border-slate-200 bg-white" />)}</div>
      ) : filteredItems.length ? (
        <>
          <div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
            {filteredItems.map((item) => {
              const open = () => navigate(materialDetailPath(item));
              const owned = Boolean(item.viewer?.isPurchased || item.viewer?.isOwned);
              const isFree = Boolean(item.isFree || Number(item.price) === 0);
              return (
                <ResourceCard
                  key={item.id}
                  image={imageUrl(item.image)}
                  title={item.title}
                  subtitle={item.author || item.subtitle}
                  badge={item.badge}
                  meta={[item.publisher?.name || item.publisher, shortDate(item.publishedAt)].filter(Boolean).join(" · ")}
                  price={item.price}
                  isFree={item.isFree}
                  owned={owned}
                  cornerClass="rounded-md"
                  compact
                  onOpen={open}
                  primaryLabel={owned ? "View My Library" : isFree ? "View details" : "Buy now"}
                  onPrimary={() => owned ? navigate("/content-library") : isFree ? open() : buyNow(item)}
                  secondaryLabel={owned || isFree ? undefined : "Add to cart"}
                  onSecondary={() => addItem(item)}
                />
              );
            })}
          </div>
          <Paginator page={page} totalPages={publicResourcesPages} onChange={(next) => setParam("page", String(next))} />
        </>
      ) : (
        <EmptyState icon={icon} title={`No ${title.toLowerCase()} found`} hint={search ? "Try another search term." : `New ${title.toLowerCase()} appear here when published.`} />
      )}
    </LearnerShell>
  );
}
